import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import QRCode from 'react-qr-code';
import { formatarMmSs } from '@/pages/loja/utils';
import { OURO } from '@/pages/loja/identidade';
import type { ValeCarteira } from '@/pages/loja/types';

export const TEMPO_VALE_MS = 20 * 60 * 1000; // 20 minutos

interface Props {
  vale: ValeCarteira;
  cancelando: boolean;
  aoCancelar: (codigo: string) => void;
}

// O vale é o que a pessoa mostra na fila: QR branco no maior tamanho possível,
// número do vale, barraca, fichas e o tempo em dourado. Cartão escuro.
export default function CartaoVale({ vale, cancelando, aoCancelar }: Props) {
  const [agora, setAgora] = useState(Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setAgora(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const expiraTs = vale.expira_em ? new Date(vale.expira_em).getTime() : NaN;
  const restante = Number.isFinite(expiraTs) ? Math.max(0, expiraTs - agora) : TEMPO_VALE_MS;
  const total = vale.itens.reduce((soma, item) => soma + item.quantidade, 0);

  return (
    <div className="overflow-hidden rounded-[16px] border border-white/15 bg-white/[0.05]">
      <div
        className="flex items-center justify-between gap-2 px-4 py-2.5"
        style={{ background: 'rgba(232,200,119,0.16)' }}
      >
        <p className="text-[12.5px] font-bold" style={{ color: OURO }}>
          Mostre este QR na barraca
        </p>
        <span className="flex h-4 w-4 items-center justify-center" style={{ color: OURO }}>
          <i className="ri-qr-scan-2-line" aria-hidden />
        </span>
      </div>

      <div className="p-4">
        {/* O vale é gerado agora: o QR entra crescendo (0.9 → 1) com fade. */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="overflow-hidden rounded-[16px] bg-white p-3"
        >
          <QRCode
            value={vale.codigo}
            size={512}
            style={{ width: '100%', height: 'auto' }}
            bgColor="#ffffff"
            fgColor="#000000"
            level="M"
          />
        </motion.div>

        <p className="mt-3 text-center text-[15px] font-bold leading-snug text-white">
          Vale nº {vale.numero} · {vale.ponto_nome} · {total} {total === 1 ? 'ficha' : 'fichas'}
        </p>

        <div className="mt-3 rounded-[14px] border border-white/10 bg-white/[0.04] py-3 text-center">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-white/45">
            Vale por
          </p>
          <p
            className="mt-0.5 text-[34px] font-extrabold leading-none tabular-nums"
            style={{ color: OURO }}
          >
            {formatarMmSs(restante)}
          </p>
          <p className="mt-0.5 text-[12px] text-white/50">para usar</p>
        </div>

        {vale.itens.length > 0 && (
          <div className="mt-3 space-y-1">
            {vale.itens.map((item) => (
              <p key={item.item_id} className="flex items-center justify-between gap-3 text-[14px]">
                <span className="min-w-0 truncate text-white/80">{item.nome}</span>
                <span className="shrink-0 font-bold text-white">{item.quantidade}×</span>
              </p>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => aoCancelar(vale.codigo)}
          disabled={cancelando}
          className="aperta mt-4 flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[14px] border border-white/15 text-[14px] font-semibold text-white/75 transition-colors hover:bg-white/[0.06] disabled:opacity-50"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-close-circle-line" aria-hidden />
          </span>
          {cancelando ? 'Cancelando…' : 'Cancelar vale'}
        </button>
      </div>
    </div>
  );
}